import { ErrorMessage } from '@hookform/error-message';
import {
  Box,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Error } from 'components/Common/Error';
import { format } from 'date-fns';
import { FC, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DATE_FORMAT_API } from '../../../../../../../../@constants/date';
import DateRangePicker from '../../../../../../../../components/DateRangePicker';
import { Calendar2Icon } from '../../../../../../../../components/SvgIcons/Calendar2Icon';
import { FileExcelIcon } from '../../../../../../../../components/SvgIcons/FileExcelIcon';
import { FilePdfIcon } from '../../../../../../../../components/SvgIcons/FilePdfIcon';
import { FooterExtraButtons } from '../../../../../../components/FooterButtons';
import { RateField } from '../../../../../../components/Inputs/RateField';
import { ReportLabel } from '../../../../../../components/ReportLabel';
import { DateTypeEnum, ReservationListReportFormModel } from '../../models';
import { CompanyField } from './fields/CompanyField';
import { CurrencyApplicationField } from './fields/currencyApplicationField';
import { CurrencyField } from './fields/CurrencyField';
import { DateTypeField } from './fields/DateTypeField';
import { FixedAtField } from './fields/FixedAtField';
import { RoomTypeField } from './fields/RoomTypeField';
import { StateField } from './fields/StateField';

export const FilterForm: FC<{
  onSubmit: (data: ReservationListReportFormModel) => void;
  onReset: () => void;
}> = ({ onSubmit, onReset }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<ReservationListReportFormModel>();

  const formValues = watch();

  const dates = useMemo(
    (): (Date | null)[] => [
      formValues?.from ? new Date(formValues?.from) : null,
      formValues?.to ? new Date(formValues?.to) : null,
    ],
    [formValues?.from, formValues?.to]
  );

  const handleChangeDates = (value: (Date | null)[]) => {
    const [from, to] = value;
    const _from = from ? format(from, DATE_FORMAT_API) : null;
    const _to = to ? format(to, DATE_FORMAT_API) : null;
    setValue('from', _from);
    setValue('to', _to);
  };

  const hideGrossRevenue = useMemo(() => {
    return formValues?.date_type === DateTypeEnum.CANCELLATION_DATE;
  }, [formValues?.date_type]);

  return (
    <Box sx={{ p: 0, height: '100%', overflowY: 'scroll' }}>
      <Stack direction={'column'} spacing={0} sx={{ width: '100%', height: '100%' }}>
        <ReportLabel label="Reservation List Report" />

        <Stack direction={'column'} spacing={2} p={3}>
          <Controller
            control={control}
            name="date_type"
            render={({ field }) => (
              <DateTypeField
                value={field.value}
                onChange={value => {
                  field.onChange(value);
                }}
              />
            )}
          />

          <DateRangePicker
            value={dates}
            onChange={handleChangeDates}
            renderInput={({ startProps, endProps }) => (
              <Stack
                direction="row"
                spacing="1rem"
                sx={{
                  width: '100%',
                }}
              >
                <TextField
                  {...startProps}
                  size="medium"
                  variant="standard"
                  sx={{
                    width: '50%',
                  }}
                  label="From"
                  error={Boolean(errors?.from)}
                  helperText={errors?.from?.message}
                  placeholder="mm/dd/yyyy"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Calendar2Icon
                          sx={{
                            fontSize: '1rem',
                            color: ({ palette }) => palette.common.black,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  {...endProps}
                  size="medium"
                  variant="standard"
                  sx={{
                    width: '50%',
                  }}
                  label="To"
                  error={Boolean(errors?.to)}
                  helperText={errors?.to?.message}
                  placeholder="mm/dd/yyyy"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Calendar2Icon
                          sx={{
                            fontSize: '1rem',
                            color: ({ palette }) => palette.common.black,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            )}
          />

          <Controller
            control={control}
            name="company"
            render={({ field }) => (
              <CompanyField value={field.value} onChange={value => field.onChange(value)} />
            )}
          />

          <Controller
            control={control}
            name="rates"
            render={({ field, formState }) => (
              <RateField
                value={field.value}
                onChange={value => field.onChange(value)}
                error={
                  formState?.errors[field.name] ? (
                    <ErrorMessage
                      errors={formState.errors}
                      name={field.name}
                      render={({ message }) => <Error>{message}</Error>}
                    />
                  ) : null
                }
              />
            )}
          />

          <Controller
            control={control}
            name="roomType"
            render={({ field }) => (
              <RoomTypeField value={field.value} onChange={value => field.onChange(value)} />
            )}
          />

          <Controller
            control={control}
            name="states"
            render={({ field }) => (
              <StateField
                value={field.value ?? null}
                onChange={value => field.onChange(value)}
                dateType={formValues?.date_type}
              />
            )}
          />

          {!hideGrossRevenue && (
            <Controller
              control={control}
              name="gross_revenue"
              render={({ field }) => (
                <FormControlLabel
                  sx={{ m: 0 }}
                  {...field}
                  checked={field.value ?? false}
                  onChange={(e, checked) => field.onChange(checked)}
                  label={
                    <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                      Gross Revenue
                    </Typography>
                  }
                  control={<Switch />}
                />
              )}
            />
          )}

          {!hideGrossRevenue && (
            <Controller
              control={control}
              name="with_revenue"
              render={({ field }) => (
                <FormControlLabel
                  sx={{ m: 0 }}
                  {...field}
                  checked={field.value ?? false}
                  onChange={(e, checked) => field.onChange(checked)}
                  label={
                    <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                      With Revenue
                    </Typography>
                  }
                  control={<Switch />}
                />
              )}
            />
          )}

          {!hideGrossRevenue && (
            <Controller
              control={control}
              name="currency"
              render={({ field, formState }) => (
                <CurrencyField
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  error={
                    formState?.errors[field.name] ? (
                      <ErrorMessage
                        errors={formState.errors}
                        name={field.name}
                        render={({ message }) => <Error>{message}</Error>}
                      />
                    ) : null
                  }
                />
              )}
            />
          )}

          {!hideGrossRevenue && (
            <Controller
              control={control}
              name="currency_application"
              render={({ field, formState }) => (
                <CurrencyApplicationField
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  error={
                    formState?.errors[field.name] ? (
                      <ErrorMessage
                        errors={formState.errors}
                        name={field.name}
                        render={({ message }) => <Error>{message}</Error>}
                      />
                    ) : null
                  }
                />
              )}
            />
          )}

          {!hideGrossRevenue && (
            <Controller
              control={control}
              name="fixed_at"
              render={({ field, formState }) => (
                <FixedAtField
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  helperText={
                    <>
                      <ErrorMessage
                        errors={formState.errors}
                        name={field.name}
                        render={({ message }) => <Error>{message}</Error>}
                      />
                    </>
                  }
                />
              )}
            />
          )}
        </Stack>

        <FooterExtraButtons
          onSubmit={file_type => {
            setValue('file_type', file_type);
            handleSubmit(onSubmit)();
          }}
          onReset={onReset}
          exportFileTypes={[
            {
              label: 'PDF',
              value: 'pdf',
              icon: <FilePdfIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
            {
              label: 'EXCEL',
              value: 'excel',
              icon: <FileExcelIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
          ]}
        />
      </Stack>
    </Box>
  );
};
